import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { spaceTemplatesService } from "@/services/spaceTemplatesService";
import { useAuth } from "@/contexts/AuthContext";
import { 
  BookOpen, BarChart3, ChefHat, Rocket, Briefcase, Heart, Palette, GraduationCap,
  Search, Sparkles, Plus, ArrowRight, Check, Star, Loader2, Crown, Dumbbell,
  Brain, FileText, Users, Target, Lightbulb, Calendar, ShoppingCart, Apple,
  Flame, Moon, Folder, Link, Wrench, Beaker, Feather, MessageCircle, Map,
  Eye, GitBranch, ClipboardCheck, UtensilsCrossed, Flower2, Unlock, MessageSquare,
  HelpCircle, List, RefreshCw, Scale, Compass, Image
} from "lucide-react";
import type { SpaceTemplate, SpaceTemplateData } from "@shared/schema";
import { TEMPLATE_CATEGORIES } from "@/data/spaceTemplates";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  BookOpen, BarChart3, ChefHat, Rocket, Briefcase, Heart, Palette, GraduationCap,
  Search, Sparkles, Plus, ArrowRight, Check, Star, Loader2, Crown, Dumbbell,
  Brain, FileText, Users, Target, Lightbulb, Calendar, ShoppingCart, Apple,
  Flame, Moon, Folder, Link, Wrench, Beaker, Feather, MessageCircle, Map,
  Eye, GitBranch, ClipboardCheck, UtensilsCrossed, Flower: Flower2, Unlock, MessageSquare,
  HelpCircle, List, RefreshCw, Scale, Compass, Image, Flask: Beaker,
};

const categoryColors: Record<string, string> = {
  "Spiritual Growth": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  "Business": "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
  "Lifestyle": "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  "Education": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  "Health": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  "Creative": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
};

const tierBadges: Record<string, { label: string; className: string }> = {
  free: { label: "Free", className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  pro: { label: "Pro", className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  premium: { label: "Premium", className: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300" },
};

interface TemplateGridProps {
  onTemplateCreated?: (spaceId: string) => void;
  showHeader?: boolean;
}

export function TemplateGrid({ onTemplateCreated, showHeader = true }: TemplateGridProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedTemplate, setSelectedTemplate] = useState<SpaceTemplate | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data: templates = [], isLoading } = useQuery<SpaceTemplate[]>({
    queryKey: ["space-templates"],
    queryFn: () => spaceTemplatesService.getPublicTemplates(),
  });

  const createFromTemplate = useMutation({
    mutationFn: async (templateId: string) => {
      if (!user?.id) throw new Error("User not authenticated");
      return spaceTemplatesService.createSpaceFromTemplate(templateId, user.id);
    },
    onSuccess: (data) => {
      toast({
        title: "Space Created",
        description: `${data.spaceName} has been created with ${data.topicsCreated} topics and ${data.agentsCreated} AI agents.`,
      });
      // Invalidate all spaces queries
      queryClient.invalidateQueries({ queryKey: ["spaces"] }); // For AppSidebar
      queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
      queryClient.invalidateQueries({ queryKey: ["/api/mastermind-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["space-templates"] });
      setDetailsOpen(false);
      if (onTemplateCreated) {
        onTemplateCreated(data.spaceId);
      } else {
        setLocation(`/spaces/${data.spaceId}`);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create space from template. Please try again.",
        variant: "destructive",
      });
    },
  });

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="h-8 w-8" /> : <Sparkles className="h-8 w-8" />;
  };

  const getSmallIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />;
  };

  const handleViewDetails = (template: SpaceTemplate) => {
    setSelectedTemplate(template);
    setDetailsOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {showHeader && (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-10 w-48" />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2" data-testid="text-templates-title">
              <Sparkles className="h-6 w-6 text-primary" />
              DEEPLY SMART Templates
            </h2>
            <p className="text-muted-foreground">
              Pre-built knowledge spaces with AI agents, topics, and prompts ready to use
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search-templates"
            />
          </div>
        </div>
      )}

      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="flex-wrap h-auto gap-1" data-testid="tabs-categories">
          {TEMPLATE_CATEGORIES.map(category => (
            <TabsTrigger 
              key={category} 
              value={category}
              data-testid={`tab-category-${category}`}
            >
              {category}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          <Sparkles className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">No templates found</h3>
          <p className="text-muted-foreground">Try adjusting your search or category filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => {
            const templateData = template.templateData as SpaceTemplateData;
            const tier = tierBadges[template.tier || "free"];
            return (
              <Card 
                key={template.id} 
                className="group hover-elevate cursor-pointer transition-all flex flex-col"
                onClick={() => handleViewDetails(template)}
                data-testid={`card-template-${template.slug}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${template.color?.replace("bg-", "").replace("-", " ")}20` }}
                    >
                      <div className={template.color?.includes("bg-") ? "" : (template.color || undefined)}>
                        {getIcon(template.icon || "Sparkles")}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {template.isFeatured === "true" && (
                        <Badge variant="secondary" className="gap-1">
                          <Star className="h-3 w-3" />
                          Featured
                        </Badge>
                      )}
                      <Badge variant="outline" className={tier.className}>
                        {template.tier === "pro" && <Crown className="h-3 w-3 mr-1" />}
                        {tier.label}
                      </Badge>
                    </div>
                  </div>
                  <CardTitle className="text-lg mt-3">{template.name}</CardTitle>
                  {template.tagline && (
                    <p className="text-sm text-primary font-medium">{template.tagline}</p>
                  )}
                  <Badge 
                    variant="outline" 
                    className={`w-fit ${categoryColors[template.category || ""] || ""}`}
                  >
                    {template.category}
                  </Badge>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <CardDescription className="line-clamp-2 mb-4">
                    {template.description}
                  </CardDescription>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mt-auto">
                    <span>{templateData?.topics?.length || 0} topics</span>
                    <span>{templateData?.agents?.length || 0} AI agents</span>
                    <span>{templateData?.prompts?.length || 0} prompts</span>
                  </div>
                  <Button 
                    className="w-full mt-4 opacity-0 group-hover:opacity-100 transition-opacity"
                    size="sm"
                    data-testid={`button-use-template-${template.slug}`}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          {selectedTemplate && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${selectedTemplate.color?.replace("bg-", "").replace("-", " ")}20` }}
                  >
                    {getIcon(selectedTemplate.icon || "Sparkles")}
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-xl">{selectedTemplate.name}</DialogTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge 
                        variant="outline" 
                        className={`${categoryColors[selectedTemplate.category || ""] || ""}`}
                      >
                        {selectedTemplate.category}
                      </Badge>
                      <Badge variant="outline" className={tierBadges[selectedTemplate.tier || "free"].className}>
                        {selectedTemplate.tier === "pro" && <Crown className="h-3 w-3 mr-1" />}
                        {tierBadges[selectedTemplate.tier || "free"].label}
                      </Badge>
                    </div>
                  </div>
                </div>
                {selectedTemplate.tagline && (
                  <p className="text-sm text-primary font-medium mt-2">{selectedTemplate.tagline}</p>
                )}
                <DialogDescription className="text-left mt-2">
                  {selectedTemplate.description}
                </DialogDescription>
              </DialogHeader>

              <ScrollArea className="max-h-[50vh]">
                <div className="space-y-6 pr-4">
                  {(() => {
                    const templateData = selectedTemplate.templateData as SpaceTemplateData;
                    return (
                      <>
                        {templateData?.lens && (
                          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                            <h4 className="font-semibold text-primary mb-2">{templateData.lens.purpose}</h4>
                            {templateData.lens.priorities && (
                              <div className="flex flex-wrap gap-2">
                                {templateData.lens.priorities.map((priority, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {priority}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {templateData?.topics && templateData.topics.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-500" />
                              Topics Included ({templateData.topics.length})
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {templateData.topics.map((topic, i) => (
                                <div 
                                  key={i} 
                                  className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                                >
                                  {getSmallIcon(topic.icon)}
                                  <div>
                                    <span className="text-sm font-medium">{topic.name}</span>
                                    {topic.description && (
                                      <p className="text-xs text-muted-foreground line-clamp-1">{topic.description}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {templateData?.agents && templateData.agents.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-500" />
                              AI Agents ({templateData.agents.length})
                            </h4>
                            <div className="space-y-2">
                              {templateData.agents.map((agent, i) => (
                                <div 
                                  key={i} 
                                  className="p-3 rounded-lg bg-muted/50"
                                >
                                  <div className="flex items-center gap-2">
                                    {agent.icon && getSmallIcon(agent.icon)}
                                    <p className="font-medium">{agent.name}</p>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{agent.description}</p>
                                  {agent.capabilities && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {agent.capabilities.map((cap, j) => (
                                        <Badge key={j} variant="outline" className="text-xs">
                                          {cap}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {templateData?.prompts && templateData.prompts.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-500" />
                              Super Prompts ({templateData.prompts.length})
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {templateData.prompts.map((prompt, i) => (
                                <div 
                                  key={i} 
                                  className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                                >
                                  {prompt.icon && getSmallIcon(prompt.icon)}
                                  <div>
                                    <span className="text-sm font-medium">{prompt.name}</span>
                                    {prompt.category && (
                                      <Badge variant="outline" className="ml-2 text-xs">
                                        {prompt.category}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {templateData?.goals && templateData.goals.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <Target className="h-4 w-4 text-blue-500" />
                              Starter Goals
                            </h4>
                            <ul className="space-y-1">
                              {templateData.goals.map((goal, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm">
                                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                  {goal}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </ScrollArea>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => createFromTemplate.mutate(selectedTemplate.id)}
                  disabled={createFromTemplate.isPending}
                  data-testid="button-create-from-template"
                >
                  {createFromTemplate.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Create Space
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
